/**
 * Path — the whole arc, point by point (REDESIGN.md §7).
 *
 * A hairline running down the left with a pin on every point. What is open is
 * a white card you can tap; what is current carries an accent border and a
 * pulsing pin; what is locked is a dashed outline with nothing in it but when
 * it opens. The no-spoiler rule from SPEC §Phase 3 still holds — the server
 * sends a locked point no title and no kind, so there is nothing here to leak.
 *
 * The tail is counted rather than listed: a hundred-odd dashed boxes would bury
 * the handful that are actually open.
 */

import { emptyState } from '../components/empty.js'
import { actCopy } from '../content/acts.js'
import { sequenceName } from '../content/kinds.js'
import { icon } from '../js/icons.js'
import { text } from '../js/html.js'
import { relativeDay, stamp } from '../js/dates.js'

/**
 * "Brief #16" — the shared sequence, numbered here rather than on the server.
 * The order is already in hand, so nothing extra has to leave the database and
 * a locked point stays as empty as the rule requires. Personal briefs sit
 * outside the count, so one person's numbering never drifts from another's.
 */
function withNumbers(timeline) {
  let number = 0

  return timeline.map((entry) => {
    if (entry.personal) {
      return { ...entry, number: null }
    }

    number += 1

    return { ...entry, number }
  })
}

/** How many dashed points are listed before the rest are simply counted. */
const LOCKED_SHOWN = 2

export function renderPath({ retreat, timeline: raw = [], acts = {} }) {
  if (retreat === null) {
    return emptyState({
      icon: 'labPath',
      title: 'Not on a retreat yet',
      body: 'Once we add you, your path opens here — day by day.',
    })
  }

  const timeline = withNumbers(raw)
  const open = timeline.filter((entry) => !entry.locked)
  const current = open.at(-1) ?? null
  const act = actCopy(current?.act, acts)

  if (timeline.length === 0) {
    return emptyState({
      icon: 'lock',
      title: 'The first brief is coming',
      body: 'Leave notifications on. We will say when something opens.',
    })
  }

  return `
    <p class="eyebrow">
      ${text(current?.act ?? 'Your path')}${current?.act_label ? ` · ${text(current.act_label)}` : ''}
    </p>
    <h1 class="h1">${text(act.title)}</h1>
    <p class="sub">${text(act.lede)}</p>

    <div class="tl">${points(timeline, retreat, current)}</div>
  `
}

function points(timeline, retreat, current) {
  const out = []
  let lockedShown = 0
  let hidden = 0

  for (const entry of timeline) {
    if (!entry.locked) {
      out.push(openPoint(entry, retreat, entry === current))

      continue
    }

    if (lockedShown < LOCKED_SHOWN) {
      lockedShown += 1
      out.push(lockedPoint(entry, retreat))
    } else {
      hidden += 1
    }
  }

  if (hidden > 0) {
    out.push(`
      <div class="node node--locked">
        <span class="node__pin" aria-hidden="true"></span>
        <div class="node__card">
          <p class="node__row"><span class="node__tag">${hidden} more points</span></p>
          <p class="node__title">Opening one by one, until day ninety</p>
        </div>
      </div>
    `)
  }

  return out.join('')
}

function openPoint(entry, retreat, isCurrent) {
  const media = mediaFor(entry)

  return `
    <div class="node ${isCurrent ? 'node--new' : 'node--done'} ${media ? 'node--media' : ''}">
      <span class="node__pin" aria-hidden="true"></span>
      <a class="node__card" href="/brief/${entry.id}" data-link>
        ${media}
        <div class="node__text">
          <p class="node__row">
            ${
              // "Brief #4" above every title is a number nobody needs twice —
              // the pins already count them. It stays on the one point that is
              // new, where it is the announcement rather than a label.
              isCurrent ? `<span class="node__tag">▸ ${text(sequenceName(entry))} · New</span>` : ''
            }
            <span class="node__date">${text(stamp(entry.publish_at, retreat.timezone))}</span>
          </p>
          <p class="node__title">${text(entry.title)}</p>
          ${
            // With a picture above it the title carries the card; the excerpt
            // would only be more text under a thing that already said it.
            media === '' && entry.excerpt
              ? `<p class="node__desc">${text(entry.excerpt)}</p>`
              : ''
          }
        </div>
      </a>
    </div>
  `
}

/**
 * What a point looks like before you open it.
 *
 * A picture when the brief has one. A dark frame with a play ring and the
 * running time when it has a video — drawn here rather than fetched from
 * YouTube, because a thumbnail from ytimg is a request to Google announcing
 * that this person opened the app today, and the privacy page says there are
 * none of those.
 *
 * Nothing at all when the brief has neither: an empty grey box is worse than
 * no box.
 */
function mediaFor(entry) {
  if (entry.image_url) {
    return `
      <span class="node__media">
        <img src="${text(entry.image_url)}" alt="" loading="lazy" decoding="async" />
        ${entry.has_video ? playBadge(entry) : ''}
      </span>
    `
  }

  if (entry.has_video) {
    return `
      <span class="node__media node__media--film">
        ${playBadge(entry)}
      </span>
    `
  }

  return ''
}

function playBadge(entry) {
  return `
    <span class="node__play">
      ${icon('play', { size: 20 })}
      ${entry.minutes ? `<b>${entry.minutes} min</b>` : ''}
    </span>
  `
}

/**
 * A locked point says when, and nothing else. The server sends no title and no
 * kind for one of these, so "Opens in 3 days" is genuinely all there is.
 */
function lockedPoint(entry, retreat) {
  return `
    <div class="node node--locked">
      <span class="node__pin" aria-hidden="true"></span>
      <div class="node__card">
        <p class="node__row">
          <span class="node__tag">${entry.personal ? 'Locked · for you only' : 'Locked'}</span>
          <span class="node__date">${text(stamp(entry.publish_at, retreat.timezone))}</span>
        </p>
        <p class="node__title">Opens ${text(relativeDay(entry.publish_at, retreat.timezone).toLowerCase())}</p>
      </div>
    </div>
  `
}
